import * as Sentry from '@sentry/vue';
import Analytics from './analytics';

/**
 * Detecta tradutor automático de browser ativo na página.
 *
 * Por que: os formulários do funil quebravam com tradutor ligado (PRD-2675) e não
 * tínhamos como saber quantos dos tickets de "Tivemos um problema" eram isso. Isto
 * transforma essa suspeita em número, e a tag no Sentry torna os erros genéricos
 * filtráveis.
 *
 * Cobertura, sendo honesto:
 * - Chrome: confiável. Ele carimba `translated-ltr`/`translated-rtl` no <html>.
 * - Edge: heurística. Não usa aquela classe; injeta `<font _msttexthidden>` e atributos
 *   `_msthash` nos nós traduzidos.
 * - Safari: traduz fora do processo da página e não deixa marcador confiável no DOM.
 *   **Vamos subcontar**, e isso é esperado — não leia o número como total.
 */
export const TRANSLATION_DETECTED_EVENT = 'Browser Translation Detected';

type TranslationEngine = 'chrome' | 'edge';

let alreadyReported = false;
let observer: MutationObserver | null = null;

function detectEngine(): TranslationEngine | null {
  const html = document.documentElement;

  if (html.classList.contains('translated-ltr') || html.classList.contains('translated-rtl')) {
    return 'chrome';
  }

  if (document.querySelector('[_msthash], font[_msttexthidden]')) return 'edge';

  return null;
}

export function stopTranslationDetection(): void {
  if (observer) observer.disconnect();
  observer = null;
}

function report(engine: TranslationEngine): void {
  if (alreadyReported) return;
  alreadyReported = true;

  Sentry.setTag('browser_translation', true);
  Sentry.setTag('browser_translation_engine', engine);

  Analytics.trackMixpanel(TRANSLATION_DETECTED_EVENT, {
    engine,
    url: window.location.pathname,
  });

  stopTranslationDetection();
}

/**
 * Dispara no máximo uma vez por sessão e para de observar assim que reporta — o dado
 * que interessa é "havia tradutor", não quantas vezes o DOM foi reescrito.
 */
export function startTranslationDetection(): void {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;

  const engine = detectEngine();
  if (engine) {
    report(engine);
    return;
  }

  observer = new MutationObserver(() => {
    const detected = detectEngine();
    if (detected) report(detected);
  });

  // o <html> cobre o caso do Chrome (classe na raiz); o subtree cobre o Edge, que só
  // se revela quando os nós traduzidos aparecem
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
    childList: true,
    subtree: true,
  });
}
